export const OrderStatus = {
  PENDING: 'PENDING',
  CONFIRMED: 'CONFIRMED',
  IN_PROGRESS: 'IN_PROGRESS',
  READY: 'READY',
  IN_DELIVERY: 'IN_DELIVERY',
  DELIVERED: 'DELIVERED',
  CANCELED: 'CANCELED',
} as const;

export type OrderStatus = typeof OrderStatus[keyof typeof OrderStatus];

export const PaymentStatus = {
  PENDING: 'PENDING',
  PAID: 'PAID',
  FAILED: 'FAILED',
  REFUNDED: 'REFUNDED',
} as const;

export type PaymentStatus = typeof PaymentStatus[keyof typeof PaymentStatus];

export const PaymentMethod = {
  ONLINE_CARD: 'ONLINE_CARD',
  SBP: 'SBP',
  SAVED_CARD: 'SAVED_CARD',
  ON_RECEIPT: 'ON_RECEIPT',
} as const;

export type PaymentMethod = typeof PaymentMethod[keyof typeof PaymentMethod];

export const DeliveryMethod = {
  DELIVERY: 'DELIVERY',
  PICKUP: 'PICKUP',
} as const;

export type DeliveryMethod = typeof DeliveryMethod[keyof typeof DeliveryMethod];

export interface OrderItem {
  id: number;
  orderId: number;
  productId: number;
  quantity: number;
  price: number;
  total: number;
  productData: any;
  product?: {
    id: number;
    name: string;
    imageUrl?: string;
    cardUrl?: string | null;
    images?: Array<{
      id: number;
      url: string;
      cardUrl?: string | null;
      productId: number;
      order: number;
    }>;
  };
}

export interface OrderStatusHistory {
  id: number;
  orderId: number;
  status: OrderStatus;
  changedBy: string;
  comment?: string;
  createdAt: string;
}

export interface RecipientData {
  phone: string;
  firstName: string;
  lastName: string | null;
}

export interface AssemblySlotData {
  id: number;
  date: string;
  hour: number;
  slotTime: string;
  bookedCount: number;
  pickupPointId: number;
}

export interface PickupPointData {
  id: number;
  lat: string;
  city: string;
  long: string;
  metro: string | null;
  address: string;
  phoneNumber: string;
  deliveryProvider?: 'MARKETPLACE' | 'SELLER';
  timezone?: string | null;
}

export interface AddressData {
  id: number;
  address: string;
  shortAddress: string;
  lat: string;
  long: string;
  tag: string | null;
  entrance: string | null;
  intercom: string | null;
  floor: string | null;
  apartment: string | null;
  comment: string | null;
}

/** Точка сборки и продавец (отдаётся с GET /orders/:id для карточки заказа). */
export interface PickupPointShopInfo {
  id: number;
  city: string;
  address: string;
  metro: string | null;
  phoneNumber: string | null;
  email: string | null;
  telegram: string | null;
  sellerId: number | null;
  deliveryProvider?: 'MARKETPLACE' | 'SELLER';
  seller: {
    id: number;
    shopName: string;
    slug: string;
  } | null;
}

export interface Order {
  id: number;
  orderNumber: string;
  userId: number;
  status: OrderStatus;
  totalAmount: number;
  subtotal: number;
  totalDiscount?: number;
  promoDiscountAmount?: number;
  deliveryAmount: number;
  discountAmount: number;
  bonusesUsed: number;
  bonusesEarned: number;
  paymentMethod?: PaymentMethod;
  paymentStatus: PaymentStatus;
  deliveryAddress?: string;
  notes?: string;
  deliverAnonymously: boolean;
  leaveAtDoor: boolean;
  callBeforeDelivery: boolean;
  deliveryMethod: DeliveryMethod;
  addressData?: AddressData;
  pickupPointData?: PickupPointData;
  pickupPointId: number;
  assemblySlotData?: AssemblySlotData;
  recipientData?: RecipientData;
  orderComment?: string;
  useBonuses: boolean;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: number;
    phone: string;
    name?: string;
    surname?: string;
  };
  items?: OrderItem[];
  statusHistory?: OrderStatusHistory[];
  readyImages?: string[];
  readyImagesApproved?: boolean;
  /** Причина отмены (при статусе CANCELED, из истории) */
  cancellationComment?: string | null;
  /** Ошибка создания заказа в Достависте (если не удалось создать доставку) */
  dostavistaCreationError?: string | null;
  /** Детализация ошибок параметров из ответа Достависты (parameter_errors) */
  dostavistaCreationParameterErrors?: unknown;
  /** Id заказа в Достависте после успешного создания */
  dostavistaOrderId?: number | null;
  /** Точка сборки и магазин (бэкенд расширяет ответ getOrder) */
  pickupPointShop?: PickupPointShopInfo | null;
  sellerAcceptanceDeadline?: string | null;
  dostavistaCourierPickupArrival?: string | null;
  dostavistaDeliveryStatus?: string | null;
  /** Вторая точка Достависты уже возвратная */
  dostavistaReturnInProgress?: boolean;
  shopName?: string | null;
}
